import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import VisitorMap from './VisitorMap';

const { FiCpu, FiFeather, FiMapPin, FiAward, FiShare2, FiClock, FiLock } = FiIcons;

const TabStory = ({ memorial }) => {
  return (
    <div className="space-y-8">
      {/* Featured Quote */}
      {memorial.featuredQuote && (
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white p-8 md:p-12 rounded-[40px] shadow-sm border border-brand-dark text-center relative overflow-hidden"
        >
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-1 bg-accent-gold/30 rounded-full" />
          <SafeIcon icon={FiFeather} className="w-10 h-10 text-accent-gold mx-auto mb-8 opacity-40" />
          <h2 className="text-2xl md:text-4xl font-serif italic text-accent-navy leading-relaxed mb-6">
            "{memorial.featuredQuote}"
          </h2>
          <div className="flex items-center justify-center gap-4 text-[10px] font-black uppercase tracking-[0.2em] text-text-muted">
            <div className="w-8 h-px bg-brand-dark" />
            Words of Wisdom
            <div className="w-8 h-px bg-brand-dark" />
          </div>
        </motion.div>
      )}

      {/* Main Story Content */}
      <div className="bg-white p-8 md:p-12 rounded-[40px] shadow-sm border border-brand-dark">
        <div className="mb-10 pb-8 border-b border-brand-dark">
          <h2 className="text-3xl font-serif text-accent-navy mb-4">A Beautiful Life</h2>
          <p className="text-xl font-serif italic text-text-muted leading-relaxed">
            "{memorial.tagline}"
          </p>
        </div>
        
        <div className="prose prose-lg max-w-none text-text-main font-light leading-loose whitespace-pre-line text-lg">
          {memorial.story}
        </div>

        <div className="mt-12 pt-8 border-t border-brand-light flex flex-col md:flex-row items-center gap-6 justify-between">
          <div className="flex items-center gap-3 text-xs text-text-muted bg-brand-light/50 px-6 py-3 rounded-2xl">
            <SafeIcon icon={FiCpu} className="w-4 h-4 text-accent-purple" />
            <span>Loving assist from our AI writing companion</span>
          </div>
          <button className="text-accent-gold font-bold text-sm flex items-center gap-2 hover:text-accent-navy transition-colors">
            Share this story <SafeIcon icon={FiShare2} className="w-4 h-4" />
          </button>
        </div>
      </div>

      <section
        aria-labelledby="story-revisions-title"
        className="rounded-3xl border border-brand-dark bg-brand-light/40 p-6 md:p-8"
      >
        <div className="flex items-start gap-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-accent-gold shadow-sm">
            <SafeIcon icon={FiClock} className="h-5 w-5" />
          </div>
          <div>
            <h3 id="story-revisions-title" className="flex items-center gap-2 font-serif text-xl text-accent-navy">
              Story revisions
              <SafeIcon icon={FiLock} className="h-4 w-4 text-text-muted" />
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-text-muted">
              Revision history is not enabled yet. The backend does not currently verify memorial-owner permissions for story revisions, so browser-only history would not be secure or sync across devices.
            </p>
            <p className="mt-3 text-xs font-semibold text-text-muted">
              Revisions can be enabled after server-side ownership and access checks are deployed.
            </p>
          </div>
        </div>
      </section>

      {/* Visitor Map Visualization */}
      <VisitorMap locations={memorial.visitorLocations} />

      {/* Life Journey Timeline */}
      {memorial.timeline && (
        <div className="bg-white p-8 md:p-12 rounded-[40px] shadow-sm border border-brand-dark">
          <h3 className="text-2xl font-serif text-accent-navy mb-12 flex items-center gap-4">
            <SafeIcon icon={FiAward} className="text-accent-gold" />
            The Journey of a Lifetime
          </h3>
          
          <div className="relative space-y-12 before:absolute before:inset-0 before:ml-5 md:before:mx-auto before:h-full before:w-0.5 before:bg-brand-dark">
            {memorial.timeline.map((item, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className={`relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group`}
              >
                {/* Icon Dot */}
                <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-accent-gold shadow-md z-10 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2">
                  <span className="text-[10px] font-black text-white">{item.year}</span>
                </div>

                {/* Content Box */}
                <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-6 rounded-3xl bg-brand-light/50 border border-brand-dark hover:border-accent-gold transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-bold text-accent-navy">{item.title}</h4>
                    <span className="md:hidden text-xs font-black text-accent-gold">{item.year}</span>
                  </div>
                  <p className="text-sm text-text-muted font-light leading-relaxed">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TabStory;